import { apiErrorMessage } from '@agos/api-client'
import {
  getGetCampaignsQueryKey,
  useGetCampaignsSendJobsJobId,
  usePostCampaignsIdIdSend,
  usePostCampaignsIdIdSendPreview,
} from '@agos/api-client/marketing'
import { T, tt } from '@agos/shared'
import { useQueryClient } from '@tanstack/react-query'
import { Send } from 'lucide-react'
import { useEffect, useState } from 'react'

import { SEGMENT_FILTER_OPTIONS } from '../marketing-customers/customer-directory'
import {
  AUDIENCE_KINDS,
  EMPTY_AUDIENCE,
  audienceLabel,
  audiencePayload,
  audienceProblems,
  jobRunning,
  jobSentence,
  previewSentence,
  sendJobOf,
  sendKindNote,
  sendPreviewOf,
  type AudienceForm,
  type AudienceKind,
  type SendKind,
  type SendPreview,
} from './campaign-send'

/**
 * *Poslať teraz* on a campaign card - `FRONTEND.md` §9.778, BACKEND.md §10A.433, owner O138d.
 *
 * To whom, how many, and how far it got. The count is the server's, asked for the audience on screen, and
 * the button is offered only for that audience: a count for one audience in front of a send to another is
 * the defect a preview exists to prevent. The send runs as a job on the server; this follows it.
 */
export function SendNowPanel({
  campaignId,
  type,
  kind,
  channels,
  writable,
  unsaved,
}: {
  campaignId: number
  type: string
  kind: SendKind
  /** The channels ticked on the card, as saved - what the send will use. */
  channels: readonly string[]
  writable: boolean
  /** The card holds changes not yet saved; the send would use the saved ones, so it waits. */
  unsaved: boolean
}) {
  const queryClient = useQueryClient()
  const [audience, setAudience] = useState<AudienceForm>(EMPTY_AUDIENCE)
  const [preview, setPreview] = useState<{ key: string; counts: SendPreview } | null>(null)
  const [asking, setAsking] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const problems = audienceProblems(audience)
  const key = JSON.stringify(audiencePayload(audience))
  const counts = preview !== null && preview.key === key ? preview.counts : null

  const ask = usePostCampaignsIdIdSendPreview({
    mutation: {
      onSuccess: (answer, variables) => {
        const parsed = sendPreviewOf(answer)
        if (parsed !== null) setPreview({ key: JSON.stringify(variables.data?.audience ?? {}), counts: parsed })
      },
      onError: (caught) => setFailure(apiErrorMessage(caught, tt('Počet sa nepodarilo zistiť.'))),
    },
  })

  const send = usePostCampaignsIdIdSend({
    mutation: {
      onSuccess: (answer) => {
        const job = sendJobOf(answer)
        setJobId(job === null ? null : job.jobId)
        setAsking(false)
      },
      onError: (caught) => {
        setAsking(false)
        setFailure(apiErrorMessage(caught, tt('Odoslanie sa nepodarilo spustiť.')))
      },
    },
  })

  const jobQuery = useGetCampaignsSendJobsJobId(jobId ?? '', {
    query: { enabled: jobId !== null, refetchInterval: (query) => (jobRunning(sendJobOf(query.state.data)) ? 1500 : false) },
  })
  const job = sendJobOf(jobQuery.data)

  // The card's history and counters move when a send ends.
  const finished = job !== null && !jobRunning(job)
  useEffect(() => {
    if (finished) void queryClient.invalidateQueries({ queryKey: getGetCampaignsQueryKey() })
  }, [finished, queryClient])

  const busy = ask.isPending || send.isPending || jobRunning(job)

  return (
    <div className="space-y-2 border-t border-border pt-3" data-send-now={type}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
        <T>Poslať teraz</T>
      </p>
      <p className="text-[11px] text-muted-foreground" data-send-kind={kind}>
        {sendKindNote(kind, type)}
      </p>

      <label className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span className="font-semibold">
          <T>Komu</T>
        </span>
        <select
          data-send-audience
          value={audience.kind}
          disabled={!writable || busy}
          onChange={(event) => {
            setAudience((current) => ({ ...current, kind: event.target.value as AudienceKind }))
            setAsking(false)
          }}
          className="min-h-[28px] rounded-lg border border-border bg-background px-2 text-xs"
        >
          {AUDIENCE_KINDS.map((choice) => (
            <option key={choice} value={choice}>
              {audienceLabel(choice)}
            </option>
          ))}
        </select>
        {audience.kind === 'segment' && (
          <select
            data-send-segment
            value={audience.segment}
            disabled={!writable || busy}
            onChange={(event) => setAudience((current) => ({ ...current, segment: event.target.value }))}
            className="min-h-[28px] rounded-lg border border-border bg-background px-2 text-xs"
          >
            {SEGMENT_FILTER_OPTIONS.filter((option) => option.value !== 'any').map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
        {(audience.kind === 'ordered' || audience.kind === 'notOrdered') && (
          <input
            data-send-days
            inputMode="numeric"
            aria-label={tt('Počet dní')}
            value={audience.days}
            disabled={!writable || busy}
            onChange={(event) => setAudience((current) => ({ ...current, days: event.target.value }))}
            className="w-16 min-h-[28px] rounded-lg border border-border bg-background px-2 text-xs"
          />
        )}
      </label>
      {problems.length > 0 && (
        <p className="text-[11px] text-red-500" data-send-invalid>
          {problems.join(' ')}
        </p>
      )}

      {channels.length === 0 && (
        <p className="text-[11px] text-red-500" data-send-no-channel>
          <T>Kampaň nemá zapnutý žiadny kanál - nie je čím poslať.</T>
        </p>
      )}
      {unsaved && (
        <p className="text-[11px] text-amber-500" data-send-unsaved>
          <T>Najprv uložte zmeny - odošle sa to, čo je uložené.</T>
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-send-preview
          disabled={!writable || busy || problems.length > 0}
          onClick={() => {
            setFailure(null)
            ask.mutate({ id: campaignId, data: { audience: audiencePayload(audience) } })
          }}
          className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium disabled:opacity-50"
        >
          <T>Koľkým to odíde?</T>
        </button>
        {counts !== null && (
          <button
            type="button"
            data-send-start
            disabled={
              !writable || busy || unsaved || channels.length === 0 || counts.recipients === 0 || problems.length > 0
            }
            onClick={() => setAsking(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-50"
          >
            <Send className="w-3 h-3" /> <T>Poslať teraz</T>
          </button>
        )}
      </div>

      {counts !== null && (
        <p className="text-[11px] text-muted-foreground" data-send-preview-counts={counts.recipients}>
          {previewSentence(counts, channels)}
        </p>
      )}

      {asking && counts !== null && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-send-confirm>
          <p className="text-xs font-semibold">
            {tt('Kampaň odíde')} {counts.recipients} {tt('zákazníkom. Odoslanie sa nedá vrátiť.')}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              data-send-cancel
              onClick={() => setAsking(false)}
              className="flex-1 px-2 py-1.5 rounded-lg border border-border text-xs font-semibold"
            >
              <T>Zrušiť</T>
            </button>
            <button
              type="button"
              data-send-go
              disabled={send.isPending}
              onClick={() => send.mutate({ id: campaignId, data: { audience: audiencePayload(audience) } })}
              className="flex-1 px-2 py-1.5 rounded-lg border border-amber-500/50 bg-amber-500/20 text-xs font-bold disabled:opacity-40"
            >
              <T>Poslať</T>
            </button>
          </div>
        </div>
      )}

      {job !== null && (
        <p
          className={job.status === 'done' || job.status === 'running' ? 'text-[11px]' : 'text-[11px] text-red-500'}
          data-send-job={job.status}
        >
          {jobSentence(job)}
        </p>
      )}
      {/* The send runs on the server whatever this read does; a read that failed says so rather than
        * leaving the last progress standing as if it were current. */}
      {jobId !== null && jobQuery.isError && (
        <p className="text-[11px] text-amber-500" data-send-job-unread>
          <T>Stav odosielania sa nepodarilo načítať - odosielanie na serveri pokračuje.</T>{' '}
          <button type="button" className="underline" onClick={() => void jobQuery.refetch()}>
            <T>Skúsiť znova</T>
          </button>
        </p>
      )}
      {failure !== null && (
        <p className="text-[11px] text-red-500" data-send-failed>
          {failure}
        </p>
      )}
    </div>
  )
}
